import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import { ContextBar } from "../components/context-bar";
import { AdvancedJson, CountChip, KeyVal, StateTag, formatAge, shortHash } from "../components/phase3";
import { ApiVsVerifiedNote, SafeError, deploymentTone, verificationLabel } from "../components/phase4";
import { EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type DeploymentReportView,
  type DeploymentStatus,
  type DeploymentView,
  type ReleaseReportContent,
} from "../lib/api";

type Banner = { tone: "ok" | "warn" | "danger"; title: string; body: string };

function banner(status: DeploymentStatus, content: ReleaseReportContent): Banner {
  const result = content.verification?.result;
  switch (status) {
    case "VERIFIED":
      return result === "VERIFIED_WITH_WARNINGS"
        ? { tone: "warn", title: "Verified with warnings", body: "The resulting state matches the approved plan, but ODIN raised warnings worth reviewing." }
        : { tone: "ok", title: "Verified", body: "ODIN re-read the live server and confirmed the resulting state matches the approved plan." };
    case "PARTIALLY_APPLIED":
      return { tone: "danger", title: "Partially applied", body: "Some operations applied but the resulting state was not fully verified. Review the failures and remediation below." };
    case "FAILED":
      return { tone: "danger", title: "Failed", body: "The deployment did not complete. No claim of a verified resulting state is made. Review the errors and remediation below." };
    case "ROLLBACK_AVAILABLE":
      return { tone: "warn", title: "Rollback available", body: "A rollback can be planned for this deployment. Rollback is a separate controlled step and is not guaranteed." };
    case "ROLLED_BACK":
      return { tone: "ok", title: "Rolled back", body: "A rollback was applied. Verify the current server state; rollback restores supported properties only." };
    case "ROLLBACK_FAILED":
      return { tone: "danger", title: "Rollback failed", body: "A rollback was attempted but did not complete. Manual review is required." };
    default:
      return { tone: "warn", title: status, body: "This is the recorded outcome for the deployment." };
  }
}

function remediation(status: DeploymentStatus, content: ReleaseReportContent): string[] {
  const out: string[] = [];
  if (content.failures.length > 0) {
    out.push("Review each failed operation below; confirm the bot still has the required permissions and role position, then regenerate a dry-run and re-approve.");
  }
  if (status === "PARTIALLY_APPLIED") {
    out.push("A partial apply means the server is in a mixed state. Consider a rollback (if available) or a fresh dry-run to reconcile.");
  }
  if (status === "FAILED") {
    out.push("Nothing was verified. If recovery data exists, a rollback may be offered; otherwise capture a fresh snapshot and re-plan.");
  }
  if (content.verification?.result === "MANUAL_REVIEW_REQUIRED") {
    out.push("Verification needs a human decision — compare the persona changes against your intent before proceeding.");
  }
  if (out.length === 0) out.push("No remediation required. Keep the plan hash and snapshot hashes for your audit trail.");
  return out;
}

// ── Presentational (testable) ────────────────────────────────────────────────
export function ReleaseReportView({
  status,
  content,
  reportHash,
  now = Date.now(),
}: {
  status: DeploymentStatus;
  content: ReleaseReportContent;
  reportHash: string;
  now?: number;
}): JSX.Element {
  const b = banner(status, content);
  const completed = content.operationResults.filter((o) => o.state === "SUCCEEDED" || o.state === "VERIFIED");
  const rollbackAvailable = content.rollbackAvailable || status === "ROLLBACK_AVAILABLE" || status === "ROLLBACK_FAILED";

  return (
    <div className="release-report">
      <div className={`report-banner report-banner--${b.tone}`} role="status">
        <strong>{b.title}</strong>
        <span>{b.body}</span>
      </div>

      <Card title="Outcome">
        <ApiVsVerifiedNote />
        <div className="kv-grid">
          <KeyVal k="Final status" v={<StatusIndicator tone={deploymentTone(status)} label={status} />} />
          <KeyVal k="Verified resulting state" v={verificationLabel(content.verification?.result)} />
          <KeyVal k="Approver" v={content.approverUserId} mono />
          <KeyVal k="Approved at" v={content.approvedAt ? formatAge(new Date(content.approvedAt).toISOString(), now) : "—"} />
          <KeyVal k="Started" v={content.startedAt ? formatAge(new Date(content.startedAt).toISOString(), now) : "—"} />
          <KeyVal k="Completed" v={content.completedAt ? formatAge(new Date(content.completedAt).toISOString(), now) : "—"} />
          <KeyVal k="Approved plan hash" v={shortHash(content.approvedPlanHash)} mono />
          <KeyVal k="Report hash" v={shortHash(reportHash)} mono />
        </div>
      </Card>

      <Card title="Before / after summary">
        <div className="kv-grid">
          <KeyVal k="Pre-deploy snapshot" v={shortHash(content.preSnapshotHash)} mono />
          <KeyVal k="Post-deploy snapshot" v={content.postSnapshotHash ? shortHash(content.postSnapshotHash) : "not captured"} mono />
          <KeyVal k="Safety policy" v={content.safetyPolicyVersion} mono />
        </div>
        <div className="row" style={{ marginTop: "0.75rem" }}>
          {Object.entries(content.operationSummary ?? {}).map(([k, v]) => (
            <CountChip key={k} label={k} value={v} tone="neutral" />
          ))}
        </div>
        {content.postSnapshotHash ? null : (
          <p className="section-note">
            No post-deploy snapshot was captured, so the resulting state could not be independently verified — a
            successful API call alone is not proof.
          </p>
        )}
      </Card>

      <Card title={`Operations (${content.operationResults.length})`}>
        <p className="section-note">
          {completed.length} completed · {content.skipped.length} skipped · {content.failures.length} failed
        </p>
        {content.operationResults.length === 0 ? (
          <p className="muted">No operation results were recorded.</p>
        ) : (
          <div className="table">
            <div className="table__head">
              <span>Resource</span>
              <span>Operation</span>
              <span>Result</span>
            </div>
            {content.operationResults.map((o, i) => (
              <div className="table__row" key={`${o.resourceKey}-${i}`}>
                <span>{o.resourceKey}</span>
                <span className="mono">{o.operationType}</span>
                <span>
                  <span className="marker">{o.state}</span>
                  {o.attempts > 1 ? <span className="marker marker--protected">{o.attempts} attempts</span> : null}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {content.failures.length > 0 ? (
        <Card title={`Errors (${content.failures.length})`}>
          {/* Safe, human-readable errors only. */}
          <div className="finding-list">
            {content.failures.map((f, i) => (
              <div className="finding-row" key={`${f.resourceKey}-${i}`}>
                <div className="op-row__head">
                  <span className="marker marker--destructive">{f.errorCode}</span>
                  <code className="op-row__key">{f.resourceKey}</code>
                  <span className="op-row__action">{f.operationType}</span>
                </div>
                <p className="finding-row__plain">{f.errorSummary}</p>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <Card title="Findings & warnings">
        {content.warnings.length === 0 && (content.verification?.warnings.length ?? 0) === 0 ? (
          <p className="muted">No warnings were raised.</p>
        ) : (
          <ul>
            {content.warnings.map((w, i) => (
              <li key={`w-${i}`}>{w}</li>
            ))}
            {(content.verification?.warnings ?? []).map((w, i) => (
              <li key={`v-${i}`}>{w}</li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Persona changes">
        {content.personaResults ? (
          <>
            <p className="section-note">
              Who can see and do what after the deploy, compared against the approved <StateTag state="proposed" />{" "}
              simulation.
            </p>
            <AdvancedJson title="Persona results (read-only)" value={content.personaResults} />
          </>
        ) : (
          <p className="muted">No persona results were recorded for this deployment.</p>
        )}
      </Card>

      <Card title="Rollback status">
        <div className="kv-grid">
          <KeyVal
            k="Rollback"
            v={
              rollbackAvailable ? (
                <StatusIndicator tone={status === "ROLLBACK_FAILED" ? "danger" : "warn"} label={status === "ROLLED_BACK" ? "rolled back" : "available"} />
              ) : status === "ROLLED_BACK" ? (
                <StatusIndicator tone="ok" label="rolled back" />
              ) : (
                <span className="muted">not available</span>
              )
            }
          />
        </div>
        {rollbackAvailable ? (
          <p className="section-note">
            Rollback is a separate, approved, controlled step and is not guaranteed to fully restore the prior state.
            Review it on the <Link to="/app/rollback-review">rollback review</Link> page.
          </p>
        ) : null}
      </Card>

      <Card title="Remediation guidance">
        <ul>
          {remediation(status, content).map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      </Card>

      <AdvancedJson title="Advanced — read-only report content (schema context)" value={content} />
    </div>
  );
}

// ── Data-fetching container ──────────────────────────────────────────────────
export function ReleaseReport(): JSX.Element {
  const ctx = useAppContext();
  const [deploymentId, setDeploymentId] = useState("");
  const [deployment, setDeployment] = useState<DeploymentView | null>(null);
  const [report, setReport] = useState<DeploymentReportView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!ctx.tenantId || deploymentId.trim().length < 1) return;
    setError(null);
    setLoading(true);
    setReport(null);
    setDeployment(null);
    const id = deploymentId.trim();
    try {
      const dep = await api.getDeployment(ctx.tenantId, id);
      setDeployment(dep);
      const rep = await api.getDeploymentReport(ctx.tenantId, id);
      setReport(rep);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load the report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader title="Release report">
        <p>
          The immutable record of what a deployment did and whether the resulting state was <strong>verified</strong> —
          not merely whether Discord returned success.
        </p>
      </PageHeader>

      {!apiConfigured ? (
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to read release reports.
        </NotConfiguredState>
      ) : (
        <>
          <ContextBar />
          {!ctx.tenantId ? (
            <EmptyState title="Select a workspace">Choose a workspace above, then open a report by deployment id.</EmptyState>
          ) : (
            <>
              <Card title="Open a report" description="Enter a deployment id to read its release report.">
                <div className="row">
                  <input
                    className="field__input"
                    placeholder="Deployment id"
                    value={deploymentId}
                    onChange={(e) => setDeploymentId(e.target.value)}
                    aria-label="Deployment id"
                  />
                  <Button variant="primary" disabled={loading || deploymentId.trim().length < 1} onClick={load}>
                    {loading ? "Loading…" : "Open report"}
                  </Button>
                </div>
              </Card>

              {error ? <SafeError message={error} /> : null}
              {loading ? <LoadingState label="Loading report…" /> : null}
              {report && deployment ? (
                <ReleaseReportView status={deployment.status} content={report.content} reportHash={report.reportHash} />
              ) : null}
            </>
          )}
        </>
      )}
    </>
  );
}
